import { Icon } from '@components/primitives/Icon'
import { Text } from '@components/primitives/Text'
import type { IconDefinition } from '@fortawesome/fontawesome-common-types'
import { faBed } from '@fortawesome/pro-regular-svg-icons/faBed'
import { faMoneyBill } from '@fortawesome/pro-regular-svg-icons/faMoneyBill'
import { faMoneyBillWave } from '@fortawesome/pro-regular-svg-icons/faMoneyBillWave'
import { faPlane } from '@fortawesome/pro-regular-svg-icons/faPlane'
import isPaidTrial from '@modules/trials/helpers/isPaidTrial'
import getReimbursementTagsFromTrial from '@modules/trials/helpers/parseKeywordsFromTrial/getReimbursementTagsFromTrial'
import type { TrialAssociation } from '@modules/trials/types/TrialAssociation'

type LogisticItem = {
  description: string
  icon: IconDefinition
  title: string
}

export const LogisticInfo = ({
  trial,
}: {
  trial: Pick<
    TrialAssociation,
    | 'briefSummary'
    | 'briefTitle'
    | 'conditions'
    | 'detailedDescription'
    | 'eligibilityCriteria'
    | 'healthyVolunteers'
    | 'phaseList'
    | 'reimbursements'
    | 'studyPopulation'
  >
}) => {
  const isPaid = isPaidTrial(trial)
  const reimbursementTags = getReimbursementTagsFromTrial(trial)

  const hasAccommodationReimbursed = reimbursementTags.includes(
    'Accommodation Reimbursement',
  )
  const hasOtherReimbursed = reimbursementTags.includes('Other Reimbursement')
  const hasTravelReimbursed = reimbursementTags.includes('Travel Reimbursement')

  const logisticItems: LogisticItem[] = [
    ...(isPaid
      ? [
          {
            description:
              'You will be compensated for participating in this trial.',
            icon: faMoneyBill,
            title: 'Participation is compensated',
          },
        ]
      : []),
    ...(hasAccommodationReimbursed
      ? [
          {
            description:
              'Your expenses for accomodation during the trial will be reimbursed.',
            icon: faBed,
            title: 'Accomodation is reimbursed',
          },
        ]
      : []),
    ...(hasTravelReimbursed
      ? [
          {
            description:
              'Your expenses for travel tickets for this trial will be reimbursed.',
            icon: faPlane,
            title: 'Travel, including flights, are covered',
          },
        ]
      : []),
    ...(hasOtherReimbursed
      ? [
          {
            description:
              'Other forms of reimbursement are provided for this trial.',
            icon: faMoneyBillWave,
            title: 'Other reimbursement is provided',
          },
        ]
      : []),
  ]

  if (logisticItems.length === 0) return null

  return (
    <div className='my-16 grid grid-cols-1'>
      <Text className='mb-4' element={'h3'} styleName='h4' value='Logistics' />
      <div>
        {logisticItems.map(
          ({ description, icon, title }: LogisticItem, key) => (
            <div className='my-3 flex' key={key}>
              <Icon className='mr-4 mt-2 text-lg' icon={icon} />
              <div>
                <Text element={'p'} styleName='p-strong' value={title} />
                <Text element={'p'} styleName='p-small' value={description} />
              </div>
            </div>
          ),
        )}
      </div>
    </div>
  )
}
